import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Header } from "../components/shared/Header";
import { Footer } from "../components/shared/Footer";
import { apiEndpoints } from "../services/api";
import { CustomPaymentFormData } from "../types";
import { CreditCard, AlertCircle, ArrowLeft, CheckCircle } from "lucide-react";
import {
  validateEmail,
  validatePhoneNumber,
  validateName,
  validateCardNumber,
  validateExpiryMonth,
  validateExpiryYear,
  validateCVV,
  ValidationResult,
} from "../lib/utils";

export const CustomPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CustomPaymentFormData>({
    first_name: "",
    last_name: "",
    email: "",
    contact_no: "",
    amount: 0,
    file_number: "",
    additional_info: "",
    card_details: {
      card_number: "",
      exp_month: "",
      exp_year: "",
      cvv: "",
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [paymentError, setPaymentError] = useState<string>("");
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState<any>(null);

  const validateField = (
    field: string,
    value: string | number
  ): ValidationResult => {
    switch (field) {
      case "first_name":
        return validateName(value as string, "First name");
      case "last_name":
        return validateName(value as string, "Last name");
      case "email":
        return validateEmail(value as string);
      case "contact_no":
        return validatePhoneNumber(value as string);
      case "amount":
        if (typeof value === "number" && value < 10) {
          return { isValid: false, message: "Minimum amount is $10" };
        }
        return { isValid: true };
      case "file_number":
        return { isValid: true };
      case "additional_info":
        return { isValid: true };
      case "card_number":
        return validateCardNumber(value as string);
      case "exp_month":
        return validateExpiryMonth(value as string);
      case "exp_year":
        return validateExpiryYear(value as string);
      case "cvv":
        return validateCVV(value as string);
      default:
        return { isValid: true };
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    if (field.startsWith("card_details.")) {
      const cardField = field.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        card_details: {
          ...prev.card_details,
          [cardField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setPaymentError("");
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate all fields
    const fieldsToValidate = [
      "first_name",
      "last_name",
      "email",
      "contact_no",
      "amount",
      "file_number",
      "additional_info",
      "card_number",
      "exp_month",
      "exp_year",
      "cvv",
    ];

    fieldsToValidate.forEach((field) => {
      let value: string | number;
      if (
        field === "card_number" ||
        field === "exp_month" ||
        field === "exp_year" ||
        field === "cvv"
      ) {
        value =
          formData.card_details[field as keyof typeof formData.card_details];
      } else {
        value = formData[field as keyof CustomPaymentFormData] as
          | string
          | number;
      }

      const validation = validateField(field, value);
      if (!validation.isValid) {
        newErrors[field] = validation.message || "Invalid input";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      console.log("errors", errors);
      return;
    }

    setIsSubmitting(true);
    setPaymentError("");

    try {
      // Prepare payment data
      const paymentData = JSON.parse(
        JSON.stringify({
          ...formData,
          card_details: {
            ...formData.card_details,
            exp_year: formData.card_details.exp_year.slice(-2), // Only last 2 digits
          },
        })
      );

      console.log("paymentData", paymentData);

      const response = await apiEndpoints.customPayment(paymentData);
      console.log("Custom payment successful:", response.data);

      setPaymentData(response.data.data);
      setPaymentSuccess(true);
    } catch (error: any) {
      console.error("Error processing custom payment:", error);
      setPaymentError(
        error.response?.data?.message ||
          "There was an error processing your payment. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success page if payment was successful
  if (paymentSuccess && paymentData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
                <CardTitle className="text-2xl text-green-800">
                  Payment Successful!
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center text-gray-700">
                  <p className="text-lg mb-2">
                    Thank you, {formData.first_name} {formData.last_name}!
                  </p>
                  <p className="mb-4">
                    Your custom payment of ${formData.amount} has been processed
                    successfully.
                  </p>
                  <div className="bg-white p-4 rounded-lg border">
                    <p className="font-semibold">Payment Details:</p>
                    <p>File Number: {formData.file_number}</p>
                    <p>Transaction ID: {paymentData.payment_charge_id}</p>
                    <p>Amount: ${formData.amount}</p>
                    <p>Date: {new Date().toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex justify-center space-x-4">
                  <Button
                    onClick={() => navigate("/consultation")}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Book Consultation
                  </Button>
                  <Button
                    onClick={() => {
                      setPaymentSuccess(false);
                      setPaymentData(null);
                      setFormData({
                        first_name: "",
                        last_name: "",
                        email: "",
                        contact_no: "",
                        amount: 0,
                        file_number: "",
                        additional_info: "",
                        card_details: {
                          card_number: "",
                          exp_month: "",
                          exp_year: "",
                          cvv: "",
                        },
                      });
                    }}
                    variant="outline"
                  >
                    Make Another Payment
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Custom Payment
            </h1>
            <p className="text-gray-600">
              Make a custom payment for your immigration services
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Payment Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    Payment Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="first_name">First Name *</Label>
                          <Input
                            id="first_name"
                            type="text"
                            value={formData.first_name}
                            onChange={(e) =>
                              handleInputChange("first_name", e.target.value)
                            }
                            className={
                              errors.first_name ? "border-red-500" : ""
                            }
                          />
                          {errors.first_name && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.first_name}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="last_name">Last Name *</Label>
                          <Input
                            id="last_name"
                            type="text"
                            value={formData.last_name}
                            onChange={(e) =>
                              handleInputChange("last_name", e.target.value)
                            }
                            className={errors.last_name ? "border-red-500" : ""}
                          />
                          {errors.last_name && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.last_name}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                              handleInputChange("email", e.target.value)
                            }
                            className={errors.email ? "border-red-500" : ""}
                          />
                          {errors.email && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.email}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="contact_no">Contact Number *</Label>
                          <Input
                            id="contact_no"
                            type="tel"
                            value={formData.contact_no}
                            onChange={(e) =>
                              handleInputChange("contact_no", e.target.value)
                            }
                            className={
                              errors.contact_no ? "border-red-500" : ""
                            }
                          />
                          {errors.contact_no && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.contact_no}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Payment Details */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Payment Details
                      </h3>
                      <div>
                        <Label htmlFor="amount">Amount (CAD) *</Label>
                        <Input
                          id="amount"
                          type="number"
                          min="10"
                          step="0.01"
                          value={formData.amount || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "amount",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className={errors.amount ? "border-red-500" : ""}
                        />
                        {errors.amount && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.amount}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          Minimum amount is $10
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="file_number">
                          File Number (Optional)
                        </Label>
                        <Input
                          id="file_number"
                          type="text"
                          value={formData.file_number}
                          onChange={(e) =>
                            handleInputChange("file_number", e.target.value)
                          }
                          className={errors.file_number ? "border-red-500" : ""}
                        />
                        {errors.file_number && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.file_number}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="additional_info">
                          Additional Information (Optional)
                        </Label>
                        <Textarea
                          id="additional_info"
                          value={formData.additional_info}
                          onChange={(e) =>
                            handleInputChange("additional_info", e.target.value)
                          }
                          className={
                            errors.additional_info ? "border-red-500" : ""
                          }
                          rows={3}
                        />
                        {errors.additional_info && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.additional_info}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Card Details */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Card Information
                      </h3>
                      <div>
                        <Label htmlFor="card_number">Card Number *</Label>
                        <Input
                          id="card_number"
                          type="text"
                          placeholder="1234 5678 9012 3456"
                          value={formData.card_details.card_number}
                          onChange={(e) =>
                            handleInputChange(
                              "card_details.card_number",
                              e.target.value
                            )
                          }
                          className={errors.card_number ? "border-red-500" : ""}
                        />
                        {errors.card_number && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.card_number}
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="exp_month">Expiry Month *</Label>
                          <Input
                            id="exp_month"
                            type="text"
                            placeholder="MM"
                            maxLength={2}
                            value={formData.card_details.exp_month}
                            onChange={(e) =>
                              handleInputChange(
                                "card_details.exp_month",
                                e.target.value
                              )
                            }
                            className={errors.exp_month ? "border-red-500" : ""}
                          />
                          {errors.exp_month && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.exp_month}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="exp_year">Expiry Year *</Label>
                          <Input
                            id="exp_year"
                            type="text"
                            placeholder="YYYY"
                            maxLength={4}
                            value={formData.card_details.exp_year}
                            onChange={(e) =>
                              handleInputChange(
                                "card_details.exp_year",
                                e.target.value
                              )
                            }
                            className={errors.exp_year ? "border-red-500" : ""}
                          />
                          {errors.exp_year && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.exp_year}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="cvv">CVV *</Label>
                          <Input
                            id="cvv"
                            type="text"
                            placeholder="123"
                            maxLength={4}
                            value={formData.card_details.cvv}
                            onChange={(e) =>
                              handleInputChange(
                                "card_details.cvv",
                                e.target.value
                              )
                            }
                            className={errors.cvv ? "border-red-500" : ""}
                          />
                          {errors.cvv && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.cvv}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Error Message */}
                    {paymentError && (
                      <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                        <p className="text-red-700">{paymentError}</p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Processing Payment...
                        </div>
                      ) : (
                        `Pay $${formData.amount || 0} CAD`
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Payment Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-8">
                <CardHeader>
                  <CardTitle>Payment Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-semibold">
                      ${formData.amount || 0} CAD
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">File Number:</span>
                    <span className="font-semibold">
                      {formData.file_number || "N/A"}
                    </span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span>${formData.amount || 0} CAD</span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 mt-4">
                    <p>• Secure payment processing</p>
                    <p>• Instant confirmation</p>
                    <p>• Receipt via email</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
